/**
 * DecisionsPerWeekTile (US-13-GR-111, spec 018 FR-12)
 *
 * Agent decisions per week over the selected window, with a Free/Basic/Pro/Premium split.
 * Counts only: no tokens, costs or model names. A failed fetch reads "Unavailable", never 0;
 * a successful window with no events is a real 0.
 */

import { useEffect, useState, startTransition } from 'react';
import { Gavel } from 'lucide-react';
import {
  DECISION_TIERS,
  getDecisionsPerWeek,
  type DecisionsPerWeekResult,
  type DecisionTier,
  type MetricWindow,
} from '../services/adminMetricsService';
import { useLatestRequest } from '../hooks/useLatestRequest';

const TIER_LABELS: Record<DecisionTier, string> = {
  free: 'Free',
  basic: 'Basic',
  pro: 'Pro',
  premium: 'Premium',
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function fmt(n: number): string {
  return n.toFixed(1);
}

const MS_PER_WEEK = 7 * MS_PER_DAY;

/**
 * Per-week rates are computed over the window itself. Weeks with no events are absent from the
 * series and the API average divides by weeks that have events only, so it is not used here.
 */
function breakdown(data: DecisionsPerWeekResult) {
  const span = (Date.parse(data.to) - Date.parse(data.from)) / MS_PER_WEEK;
  const weeksInWindow = Number.isFinite(span) && span > 0 ? span : 1;
  const perWeek = (sum: number) => sum / weeksInWindow;

  const sumTotal = data.weeks.reduce((s, w) => s + w.totalDecisions, 0);
  const unresolvedSum = data.weeks.reduce((s, w) => s + w.tierUnresolved, 0);
  const splitAvailable = data.weeks.every((w) => w.byTier !== null);
  const tierSums = DECISION_TIERS.map((t) => ({
    tier: t,
    sum: data.weeks.reduce((s, w) => s + (w.byTier?.[t] ?? 0), 0),
  }));
  const allTiersZero = tierSums.every((t) => t.sum === 0);
  const tiers =
    splitAvailable && !(allTiersZero && unresolvedSum > 0)
      ? tierSums.map(({ tier, sum }) => ({ tier, value: perWeek(sum) }))
      : null;
  return { headline: perWeek(sumTotal), tiers, unresolved: perWeek(unresolvedSum) };
}

export function DecisionsPerWeekTile({ window: days }: { window: MetricWindow }) {
  const [data, setData] = useState<DecisionsPerWeekResult | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const beginLoad = useLatestRequest();

  useEffect(() => {
    const isCurrent = beginLoad();
    const to = new Date();
    const from = new Date(to.getTime() - days * MS_PER_DAY);
    const load = async () => {
      setLoading(true);
      setFailed(false);
      try {
        const result = await getDecisionsPerWeek(from, to);
        if (isCurrent()) setData(result);
      } catch {
        if (isCurrent()) {
          setData(null);
          setFailed(true);
        }
      } finally {
        if (isCurrent()) setLoading(false);
      }
    };
    startTransition(() => {
      void load();
    });
  }, [days, beginLoad]);

  const parts = data ? breakdown(data) : null;

  return (
    <section
      aria-labelledby="decisions-per-week-heading"
      className="p-5 bg-zinc-900 rounded-xl border border-zinc-800"
    >
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-violet-500/10 text-violet-400">
          <Gavel className="w-4 h-4" aria-hidden="true" />
        </div>
        <div>
          <h2 id="decisions-per-week-heading" className="text-sm font-semibold text-zinc-100">
            Decisions per week
          </h2>
          <p className="text-xs text-zinc-500">Per week, last {days} days</p>
        </div>
      </div>

      {loading && (
        <p className="mt-4 text-xs text-zinc-500" role="status">
          Loading decisions per week…
        </p>
      )}

      {!loading && failed && (
        <p className="mt-4 text-xl font-semibold text-zinc-400" data-testid="decisions-per-week-value">
          Unavailable
        </p>
      )}

      {!loading && data && parts && (
        <div className="mt-4">
          <p className="text-xl font-semibold text-zinc-100" data-testid="decisions-per-week-value">
            {fmt(parts.headline)}
          </p>
          <p className="text-xs text-zinc-500">decisions per week, all tiers</p>

          {parts.tiers ? (
            <dl className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {parts.tiers.map(({ tier, value }) => (
                <div key={tier} className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-800">
                  <dt className="text-xs text-zinc-500">{TIER_LABELS[tier]} tier</dt>
                  <dd className="text-sm font-semibold text-zinc-100">{fmt(value)}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="mt-3 text-xs text-zinc-500">Tier split unavailable</p>
          )}

          {parts.unresolved > 0 && (
            <p className="mt-2 text-xs text-zinc-500">
              Unresolved tier: {fmt(parts.unresolved)} per week
            </p>
          )}
        </div>
      )}
    </section>
  );
}
